import { DocumentNode } from "@apollo/client";
import { useModal } from "@ebay/nice-modal-react";
import { addMonths, getUnixTime } from "date-fns/esm";
import { Controller, FormProvider, SubmitHandler, useForm } from "react-hook-form";

import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Datepicker from "ds/components/Datepicker";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";
import { stringIsRequired, validateOptionalDateIsAfterNow } from "utils/formValidators";

import { PersonalSettingsApiKeysCreateFields } from "./types";
import usePersonalApiKeysCreate from "./useCreate";

type PersonalSettingsApiKeysCreateDrawerProps = {
  refetchQueries?: DocumentNode[];
};

const PersonalSettingsApiKeysCreateDrawer = createDrawer(
  ({ refetchQueries }: PersonalSettingsApiKeysCreateDrawerProps) => {
    const drawer = useModal();

    const trackSegmentAnalyticsEvent = useAnalytics({
      page: AnalyticsPagePersonal.PersonalApiKeys,
    });

    const createForm = useForm<PersonalSettingsApiKeysCreateFields>({
      defaultValues: {
        name: "",
        description: "",
        expiresAt: undefined,
      },
      mode: "onChange",
    });

    const {
      register,
      handleSubmit,
      formState: { errors, isDirty },
    } = createForm;

    const handleCloseDrawer = () => {
      drawer.resolve();
      drawer.hide();
    };

    const { onCreate, loading } = usePersonalApiKeysCreate({
      refetchQueries,
      callback: handleCloseDrawer,
    });

    const onSubmit: SubmitHandler<PersonalSettingsApiKeysCreateFields> = (formFields) => {
      trackSegmentAnalyticsEvent("Create Confirm");
      onCreate({
        name: formFields.name.trim(),
        description: formFields.description.trim(),
        expiresAt: formFields.expiresAt ? getUnixTime(formFields.expiresAt) : null,
      });
    };

    const handleSubmitClick = () => {
      handleSubmit(onSubmit)();
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...createForm}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Create API key" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <FormField label="Name" error={errors.name?.message}>
              {({ ariaInputProps }) => (
                <Input
                  placeholder="Enter API key name"
                  error={!!errors.name}
                  {...register("name", {
                    validate: stringIsRequired("Name field is required."),
                  })}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <Controller
              name="expiresAt"
              rules={{
                validate: validateOptionalDateIsAfterNow,
              }}
              render={({ field, fieldState }) => (
                <Box direction="column" gap="medium" margin="large 0 0 0">
                  <FormToggleField
                    variant="switch"
                    title="Expiration"
                    checked={!!field.value}
                    onChange={(checked) => {
                      field.onChange(checked ? addMonths(new Date(), 1) : undefined);
                    }}
                  />
                  {field.value && (
                    <FormField error={fieldState.error?.message} noMargin>
                      <Datepicker
                        date={field.value}
                        onChange={field.onChange}
                        minDate={new Date()}
                        withTime
                        fullWidth
                      />
                    </FormField>
                  )}
                </Box>
              )}
            />

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter API key description here..."
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <Box margin="large 0 0 0">
              <Banner variant="info">
                Upon API key creation, a file will be generated with API key secret token details.
                Please save this file somewhere safe as it will never be available again.
              </Banner>
            </Box>

            <DrawerFooter>
              <DrawerFooterActions>
                <Button variant="secondary" onPress={handleCloseDrawer}>
                  Cancel
                </Button>
                <Button variant="primary" onPress={handleSubmitClick} loading={loading}>
                  Create
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showPersonalSettingsApiKeysCreateDrawer = createDrawerTrigger(
  PersonalSettingsApiKeysCreateDrawer
);
